import * as React from "react";
import { QueryRoute } from "bernie-core";
import { Layer } from "bernie-view";
import { formatText } from "bernie-l10n";
import { observer, inject } from "mobx-react";
import { action, observable } from "mobx";

import { DestinationSampleReviews } from "./DestinationSampleReviews";
import { DestinationOverallRating } from "./DestinationOverallRating";
import { DestinationUserReviewsDialog } from "./DestinationUserReviewsDialog";
import { JumplinkItem } from "components/flexComponents/Jumplinks/Jumplinks";

import { DestinationUserReviewsProps, DestinationUserReviewsViewProps } from "./typings";

enum View {
  DEFAULT = "default",
  DETAIL = "detail"
}

@inject("routeStore", "dx")
@observer
export class DestinationUserReviews extends React.Component<DestinationUserReviewsProps> {
  private readonly jumplinkRef: React.RefObject<HTMLDivElement>;
  private jumplink: JumplinkItem;

  constructor(props: DestinationUserReviewsProps) {
    super(props);

    this.jumplinkRef = React.createRef();
    this.showMoreReviews = this.showMoreReviews.bind(this);
    this.toggleAllDestinationReviews = this.toggleAllDestinationReviews.bind(this);
  }

  @observable
  private maxVisibleReview = observable.box(20);

  @action
  public showMoreReviews() {
    this.maxVisibleReview.set(this.maxVisibleReview.get() + 20);
  }

  private isDialogOpen = (): boolean => {
    const routeStore = this.props.routeStore;
    let isCurrentlyOpen = false;

    if (routeStore) {
      isCurrentlyOpen =
        routeStore.queryParams.destDialogReviews === `open-${this.props.flexComponent.id}`;
    }

    return isCurrentlyOpen;
  };

  @action
  public toggleAllDestinationReviews() {
    const routeStore = this.props.routeStore;

    if (routeStore) {
      if (this.isDialogOpen()) {
        routeStore.closeDialog("destDialogReviews");
      } else {
        routeStore.updateSearch({ destDialogReviews: `open-${this.props.flexComponent.id}` });
      }
    }
  }

  /* istanbul ignore next line */
  public componentDidMount() {
    const flexComponent = this.props.flexComponent;
    const model = flexComponent.model;

    if (model.view === View.DETAIL) {
      this.jumplink = {
        identifier: 3,
        name: "Reviews",
        icon: "photo_library",
        reference: this.jumplinkRef,
        localisedName: formatText("jumplinks.reviews")
      };

      const checkForExistingReference = (reference: JumplinkItem) => {
        return reference["name"] === this.jumplink.name;
      };

      const addUpdatedReference = (reference: JumplinkItem) => {
        if (this.props.dx && reference["name"] === this.jumplink.name) {
          const index = this.props.dx.jumplinksArray.indexOf(reference);
          this.props.dx.jumplinksArray.splice(index, 1);
          this.props.dx.jumplinksArray.push(this.jumplink);
        }
      };

      if (this.props.dx) {
        if (
          this.props.dx.jumplinksArray.some((reference: JumplinkItem) =>
            checkForExistingReference(reference)
          )
        ) {
          this.props.dx.jumplinksArray.map((reference: JumplinkItem) =>
            addUpdatedReference(reference)
          );
        } else {
          this.props.dx.jumplinksArray.push(this.jumplink);
        }
      }
    }
  }

  /* istanbul ignore next line */
  public componentWillUnmount() {
    if (this.props.dx) {
      if (this.props.dx.jumplinksArray.includes(this.jumplink)) {
        this.props.dx.jumplinksArray.splice(this.props.dx.jumplinksArray.indexOf(this.jumplink), 1);
      }
    }
  }

  public render() {
    return (
      <DestinationUserReviewsView
        flexComponent={this.props.flexComponent}
        context={this.props.context}
        toggleAllDestinationReviews={this.toggleAllDestinationReviews}
        showMoreReviews={this.showMoreReviews}
        maxVisibleReview={this.maxVisibleReview}
        dx={this.props.dx}
        jumplinkRef={this.jumplinkRef}
      />
    );
  }
}

export const DestinationUserReviewsView = observer((props: DestinationUserReviewsViewProps) => {
  const { flexComponent } = props;
  const { model } = flexComponent;
  const { destinationName, reviews, aggregateReview, countPerRating } = model;

  if (!(destinationName && reviews && reviews.length && aggregateReview && countPerRating)) {
    return null;
  }

  const { totalReviews, totalRatings, averageRating } = aggregateReview;

  if (!(totalReviews && totalRatings && averageRating)) {
    return null;
  }

  const nameForTracking = "TG.LP.Dest.UserReviews";

  return (
    <>
      {model.view === View.DETAIL ? (
        <div
          className="DestinationUserReviews uitk-cell all-cell-1-1 detailView"
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          ref={props.jumplinkRef}
        >
          <DestinationSampleReviews
            destinationName={destinationName}
            reviews={reviews}
            totalReviews={totalReviews}
            totalRatings={totalRatings}
            avgRating={averageRating}
            countPerRating={countPerRating}
            toggleAllDestinationReviews={props.toggleAllDestinationReviews}
            nameForTracking={nameForTracking}
          />
        </div>
      ) : (
        <div
          className="DestinationUserReviews uitk-cell all-grid-align-center defaultView"
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
        >
          <DestinationOverallRating
            totalReviews={totalReviews}
            avgRating={averageRating}
            toggleAllDestinationReviews={props.toggleAllDestinationReviews}
            nameForTracking={nameForTracking}
          />
        </div>
      )}
      <QueryRoute query={{ destDialogReviews: `open-${flexComponent.id}` }}>
        <Layer id="destination-reviews" onDismiss={props.toggleAllDestinationReviews}>
          <DestinationUserReviewsDialog
            toggleAllDestinationReviews={props.toggleAllDestinationReviews}
            showMoreReviews={props.showMoreReviews}
            reviews={reviews}
            maxVisibleReview={props.maxVisibleReview}
            reviewDialogTitle={formatText("destinationReviews.cityReviews", destinationName)}
            nameForTracking={nameForTracking}
          />
        </Layer>
      </QueryRoute>
    </>
  );
});

export default DestinationUserReviews;
